<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useAuthStore } from "~/plugins/store/auth";
import { translation, useTranslationStore } from "~/plugins/store/translations";
import type { TranslationList } from "~/types/Utils";
import { useNuxtApp } from "#app";
import { useUserStore } from "~/plugins/store/user";
import { useReCaptcha } from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import { useVehicleNoteStore } from "~/plugins/store/vehicle-note";
import {
  createDefaultValidationVehicleNote,
  createDefaultVehicleNote,
  type ValidationVehicleNote,
  type VehicleNote
} from "~/types/VehicleNote";
import { recaptcha } from "~/composables/recaptcha";
import { type ApiVehicleDetails, createDefaultApiVehicleDetails } from "~/types/Vehicle";
import { useVehicleStore } from "~/plugins/store/vehicle";
import VehicleText from "~/components/rca/VehicleText.vue";
import VehicleEventAmount from "~/components/vehicle/VehicleEventAmount.vue";
import VehicleReminderDate from "~/components/vehicle/VehicleReminderDate.vue";

export default defineComponent({
  components: {VehicleReminderDate, VehicleEventAmount, VehicleText, AccountLayout, LoadingDialog },
  setup() {
    definePageMeta({
      middleware: ['auth'],
    });

    useHead({
      title: 'Xiga | Notițe de mașină',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const { $toast } = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const route = useRoute();
    const vehicleId = parseInt(route.params.id as string, 10);
    const vehicleNoteStore = useVehicleNoteStore();
    const vehicleStore = useVehicleStore();
    const vehicle = ref(createDefaultApiVehicleDetails() as ApiVehicleDetails | null);
    const noteForm = ref<VehicleNote>(createDefaultVehicleNote());
    const validationVehicleNote = ref<ValidationVehicleNote>(createDefaultValidationVehicleNote());

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (accessToken) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = 'Se încarcă datele...';
        vehicle.value = await vehicleStore.getVehicle(vehicleId, accessToken);
        showLoadingDialog.value = false;
        if (vehicle.value) {
          noteForm.value.vehicle_id = vehicle.value.id;
        }
      }
    });

    const handleValidationVehicleNoteStatus = ({ field, isValid }: { field: string, isValid: boolean }) => {
      validationVehicleNote.value[field] = isValid;
    };

    const getManufacturerLogo = (manufacturer: string) => {
      const imagePath = '/images/cars/' + manufacturer + '.png';
      return imageExists(imagePath) ? imagePath : '/images/cars/no-make.png';
    };

    const imageExists = (url: string) => {
      try {
        const http = new XMLHttpRequest();
        http.open('HEAD', url, false);
        http.send();
        return http.status !== 404;
      } catch (error) {
        return false;
      }
    };

    const getExpirationClass = (expirationDate: string | null | undefined) => {
      if (!expirationDate) return 'text-red';
      const now = new Date();
      const expiration = new Date(expirationDate);
      const diffTime = expiration.getTime() - now.getTime();
      const diffDays = diffTime / (1000 * 3600 * 24);

      if (diffDays < 0) {
        return 'text-red';
      } else if (diffDays < 60) {
        return 'text-warning';
      } else {
        return 'text-success';
      }
    };

    const createVehicleNote = async () => {
      let isFormValid = true;

      // Check validation status for each field
      for (const [field, isValid] of Object.entries(validationVehicleNote.value)) {
        if (!isValid) {
          console.log(`Validation failed for field: ${field}`);
          window.dispatchEvent(new CustomEvent('validate-vehicle', { detail: field }));
          isFormValid = false;
        }
      }

      // If the form is not valid, do not proceed
      if (!isFormValid) {
        $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
        return;
      }

      // Proceed with the creation if the form is valid
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.creating_note, 'Se creează notița...');

      try {
        const token = <string>await recaptcha(recaptchaInstance);
        const response = await vehicleNoteStore.createNote(noteForm.value, <string>accessToken, token);
        if (response) {
          $toast.success(translation(translationList.value.note_created, 'Notița a fost creată cu succes.'));
          navigateTo('/account/vehicles/' + vehicleId + '/notes/');
        } else {
          $toast.error(translation(translationList.value.error, 'A apărut o eroare.'));
        }
      } catch (error) {
        console.error('Error creating note:', error);
        $toast.error(translation(translationList.value.error, 'A apărut o eroare.'));
      } finally {
        showLoadingDialog.value = false;
      }
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      showLoadingDialog,
      messageLoadingDialog,
      getManufacturerLogo,
      getExpirationClass,
      vehicle,
      handleValidationVehicleNoteStatus,
      noteForm,
      createVehicleNote
    };
  }
});
</script>

<template>
  <account-layout class="profile">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <v-container class="content-container" v-if="vehicle">
      <v-container fluid class="component-container-1400mw">
        <v-container class="background-ghost-white" style="border-radius: 15px;">
          <v-row class="mt-3">
            <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
              <h3 class="text-primary">
                <span class="cursor-pointer" @click="navigateTo('/account/vehicles/' + vehicle.id + '/')">
                  {{ vehicle.rca_vehicle_make_name }} {{ vehicle.rca_vehicle_model_name }}
                </span>
                <v-icon>mdi-chevron-right</v-icon>
                <span class="cursor-pointer" @click="navigateTo('/account/vehicles/' + vehicle.id + '/notes/')">
                  {{ translation(translationList.vehicle_notes, 'Notițe de mașină') }}
                </span>
                <v-icon>mdi-chevron-right</v-icon>
                {{ translation(translationList.vehicle_notes, 'Adaugă notiță') }}
              </h3>
              <p class="font-weight-regular">
                <span class="font-weight-bold">{{ vehicle.registration_plate }} {{ vehicle.vin }}</span>.
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card>
                <v-card-title>
                  <h4 class="text-primary">
                    {{ translation(translationList.vehicle_notes, 'Adaugă notiță') }}
                  </h4>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <vehicle-event-date
                          v-model="noteForm.event_date"
                          @validation-vehicle-status="handleValidationVehicleNoteStatus"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <vehicle-reminder-date
                          v-model="noteForm.reminder_date"
                          @validation-vehicle-status="handleValidationVehicleNoteStatus"/>
                    </v-col>
                    <v-col cols="12">
                      <vehicle-note-event-type-select
                          v-model="noteForm.event_type"
                          @validation-vehicle-status="handleValidationVehicleNoteStatus"/>
                    </v-col>
                    <v-col cols="12">
                      <vehicle-text
                          v-model="noteForm.text"
                          @validation-vehicle-status="handleValidationVehicleNoteStatus"/>
                    </v-col>
                  </v-row>
                  <v-row class="mt-10 mb-15">
                    <v-col cols="12" class="d-flex align-center justify-center">
                      <v-btn
                          class="block-on-mobile"
                          color="primary"
                          size="x-large"
                          flat
                          rounded="lg"
                          @click="createVehicleNote"
                      >
                        {{ translation(translationList.view, 'Salvează') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-container>
    </v-container>
  </account-layout>
</template>

<style scoped>

</style>
